<template>
  <div class="budget-per-development-strategy">
    <template
      v-for="budgetType in budgetTypes"
      :key="budgetType"
    >
      <!-- Operating shares the section wrapper's anchor; capital gets its own so the sidebar can target each table -->
      <h2
        :id="budgetType === 'capital' ? 'capital-budget' : undefined"
        class="!mb-1"
      >
        Summary of expenditure by development strategy - {{ budgetType }} budget
      </h2>

      <!-- Editor-only guidance naming the National Treasury table to source from; excluded from the published PDF -->
      <p class="table-reference">
        {{ tableReferences[budgetType] }}
      </p>

      <div
        v-if="budgetType === 'capital'"
        class="light-chrome-banner mt-2 mb-4"
      >
        <strong>Please Note:</strong> This table is automatically calculated by summing the capital budgets of the projects linked to each development strategy.
      </div>

      <AppTable class="financial-plan-table mb-8">
        <AppTableHeader>
          <th class="w-[30%]">
            <span class="capitalize">{{ budgetType }}</span> budget (R'000)
          </th>

          <th
            v-for="year in years"
            :key="year.key"
            class="!text-left"
          >
            {{ year.label }}
          </th>
        </AppTableHeader>

        <AppTableBody
          :colspan="years.length + 1"
          :no-results="strategies.length === 0"
          no-results-text="No strategies found"
        >
          <tr
            v-for="strategy in strategies"
            :key="strategy.id"
          >
            <td>
              {{ strategy.name }}
            </td>

            <td
              v-for="year in years"
              :key="year.key"
            >
              <!-- Operating budgets are captured manually; capital budgets stay calculated from projects -->
              <input
                v-if="budgetType === 'operating'"
                type="text"
                inputmode="decimal"
                :value="formatValue(getOperatingValue(strategy.id, year.key))"
                @keypress="allowOnlyNumbers"
                @change="updateOperatingValue($event, strategy.id, year.key)"
              >
              <template v-else>
                {{ formatValue(getCapitalTotal(strategy.id, year.key)) }}
              </template>
            </td>
          </tr>
        </AppTableBody>

        <!-- This ensures this row is always included even when there are no development strategies present -->
        <tbody>
          <tr>
            <td class="font-semibold">
              Other expenditure
            </td>
            <td
              v-for="year in years"
              :key="year.key"
            >
              <input
                type="text"
                inputmode="decimal"
                :value="formatValue(getNonProjectValue(budgetType, year.key))"
                @keypress="allowOnlyNumbers"
                @change="updateNonProjectValue($event, budgetType, year.key)"
              >
            </td>
          </tr>
        </tbody>
      </AppTable>
    </template>

    <div class="light-chrome-banner mt-4">
      <strong>Please Note:</strong> To help us move forward, please complete the sections highlighted in yellow. Please fill in all values, including those amounting to zero rands, to ensure the form is processed correctly.
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';
import { useStringFormatter } from '@/composables';
import { mtrefYearHeadings } from '@/const';
import { usePlanStore } from '@/store';

const planStore = usePlanStore();
const stringFormatter = useStringFormatter();

// Both tables cover the three MTREF years, stored under positional year keys.
const years = mtrefYearHeadings.map((label, index) => ({ key: `year${index + 1}`, label }));

// Builds a row carrying every year key, so seeded rows stay in step with `years` and
// no year surfaces as undefined downstream.
const yearRow = (value) => Object.fromEntries(years.map(({ key }) => [key, value]));

const budgetTypes = ['operating', 'capital'];

// National Treasury table each budget table is sourced from.
const tableReferences = {
  operating: 'Table SA5',
  capital: 'Table SA6',
};

// Development strategies drive the rows of both budget tables.
const strategies = computed(() => planStore.content.developmentStrategies || []);

// Capital budgets remain automatically calculated by summing the capital budget of
// every project linked to each development strategy.
const capitalTotals = computed(() => {
  const totals = {};

  strategies.value.forEach(strategy => {
    totals[strategy.id] = yearRow(0);
  });

  const sections = planStore.content.implementationPlan?.municipalProgrammesAndProjects || [];

  sections.forEach(section => {
    (section.projects || []).forEach(project => {
      const entry = totals[project.developmentStrategyId];

      // Only sum projects linked to a strategy that is still present
      if (entry && project.budget) {
        years.forEach(({ key }) => {
          entry[key] += parseFloat(project.budget.capital?.[key]) || 0;
        });
      }
    });
  });

  return totals;
});

const getCapitalTotal = (strategyId, yearKey) => {
  return capitalTotals.value[strategyId]?.[yearKey] ?? 0;
};

const getOperatingValue = (strategyId, yearKey) => {
  const entry = planStore.content.financialPlan?.operatingBudgetByStrategy
    ?.find(item => item.developmentStrategyId === strategyId);

  return entry?.[yearKey] ?? null;
};

const updateOperatingValue = (e, strategyId, yearKey) => {
  const raw = e.target.value.replace(/[^0-9.-]/g, '');
  const numericValue = raw === '' ? null : parseFloat(raw);

  // Ensure the array exists (existing plans may predate this field)
  if (!planStore.content.financialPlan.operatingBudgetByStrategy) {
    planStore.content.financialPlan.operatingBudgetByStrategy = [];
  }

  const entries = planStore.content.financialPlan.operatingBudgetByStrategy;
  let entry = entries.find(item => item.developmentStrategyId === strategyId);

  // Create a zeroed entry the first time this strategy's operating budget is edited
  if (!entry) {
    entry = { developmentStrategyId: strategyId, ...yearRow(null) };
    entries.push(entry);
  }

  entry[yearKey] = numericValue;
};

const getNonProjectValue = (budgetType, yearKey) => {
  return planStore.content.financialPlan?.otherExpenditure?.[budgetType]?.[yearKey] ?? null;
};

const allowOnlyNumbers = (e) => {
  if (!/[\d.-]/.test(e.key) && e.keyCode > 31) e.preventDefault();
  if (e.key === '.' && e.target.value.includes('.')) e.preventDefault();
};

const updateNonProjectValue = (e, budgetType, yearKey) => {
  const raw = e.target.value.replace(/[^0-9.-]/g, '');
  const numericValue = raw === '' ? null : parseFloat(raw);

  // Ensure the nested path exists before writing (handles existing plans missing the key)
  if (!planStore.content.financialPlan.otherExpenditure) {
    planStore.content.financialPlan.otherExpenditure = {
      operating: yearRow(null),
      capital: yearRow(null),
    };
  }

  planStore.content.financialPlan.otherExpenditure[budgetType][yearKey] = numericValue;
};

const formatValue = (value) => {
  return stringFormatter.financialTableFormatters['currency'].format(value);
};
</script>
